import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {MentalReel} from "./MentalReel";
import {Mental} from "../Mental";
import {MentalReelSet} from "./MentalReelSet";
import {MentalGameAssets} from "../MentalGameAssets";
import {MentalSymbol} from "./MentalSymbol";
import {Power4, TimelineLite} from "gsap";
import {MentalGameMode} from "../server/MentalGameMode";

export class MentalLayerSet {
    public layers: PIXI.Container[];
    public position: PIXI.ObservablePoint;

    constructor(name: string, count: number = 3) {
        this.layers = [];

        for (let i: number = 0; i < count; i++) {
            const layer = new PIXI.Container();
            layer.name = name + "_" + i;
            this.layers.push(layer);
        }

        this.position = new PIXI.ObservablePoint(this.onPosition, this);
    }

    public onPosition(): void {
        for (let layer of this.layers) {
            layer.position.set(this.position.x, this.position.y);
        }
    }

    public addLayerSet(set: MentalLayerSet): void {
        for (let i: number = 0; i < set.layers.length; i++) {
            this.addToLayer(i, set.layers[i]);
        }
    }

    public removeLayerSet(set: MentalLayerSet): void {
        for (let i: number = 0; i < set.layers.length; i++) {
            this.removeFromLayer(i, set.layers[i]);
        }
    }

    public addToLayer<TChildren extends PIXI.DisplayObject[]>(index: number, ...child: TChildren): TChildren[0] {
        return this.layers[index].addChild(...child);
    }

    public removeFromLayer<TChildren extends PIXI.DisplayObject[]>(index: number, ...child: TChildren): TChildren[0] {
        return this.layers[index].removeChild(...child);
    }
}

export class MentalReelAreaView extends PIXI.Container implements StageLayer {
    private _reelBackground: PIXI.Sprite;
    private _bottomLocker1: PIXI.Sprite;
    private _bottomLocker2: PIXI.Sprite;
    private _waysText: PIXI.Sprite;
    private _reelMask: PIXI.Graphics[] = [];
    private _mainView: PIXI.Container;
    private _reelLayers: MentalLayerSet;
    private symbolsSize = new PIXI.Point(144, 198);

    constructor() {
        super();
        this.name = "MENTAL_REEL_AREA_VIEW";
        this.init();
    }

    private _reels: MentalReel[];

    get reels(): MentalReel[] {
        return this._reels;
    }

    public get reelPosition(): PIXI.Point {
        return new PIXI.Point(this._mainView.position.x, this._mainView.position.y);
    }

    public getReels(ids: number[]): MentalReel[] {
        const result: MentalReel[] = []
        for (let id of ids) {
            result.push(this.getReel(id));
        }
        return result;
    }

    public reelAreaMask(specialReels: boolean[] = [false, false]): void {
        const specialReelOne: number = specialReels[0] ? this.symbolsSize.y * 2.5 : this.symbolsSize.y * 2;
        const specialReelTwo: number = specialReels[1] ? this.symbolsSize.y * 2.5 : this.symbolsSize.y * 2;
        this._reelMask[1].height = specialReelOne;
        this._reelMask[3].height = specialReelTwo;
    }

    public onOrientationChanged(): void {
        this._mainView.position.set(0, 90);
    }

    public getReel(id: number): MentalReel {
        return this._reels[id];
    }

    public getAllVisibleSymbols(ofType?: string): MentalSymbol[] {
        let symbols: MentalSymbol[] = [];
        for (let reel of Mental.reelArea.reels) {
            symbols = symbols.concat(reel.getAllVisibleSymbols(ofType));
        }

        return symbols;
    }

    public nudgeSymbol(sym: PIXI.DisplayObject, reel: number, steps: number, stepSize: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const posY: number = sym.position.y;
        tl.to(sym.position, {
            duration: 0.1,
            y: posY - stepSize,
            ease: Power4.easeIn
        }).eventCallback("onComplete", () => {
            sym.position.y = posY - stepSize;
        });

        return tl;
    }

    public onChangeMode(mode: string): void {
        switch (mode) {
            case MentalGameMode.SUPER_FREESPIN:
                this._waysText.tint = 0xffffff;
                break;
            default:
                this._waysText.tint = 0xaaaaaa;
                break;
        }
    }

    private init(): void {
        this._mainView = new PIXI.Container();
        this._mainView.name = "ReelAreaView"

        // reel background
        this._reelBackground = new PIXI.Sprite(Mental.assetLoader.getTextures(MentalGameAssets.REEL_BACKGROUND)[0]);
        this._reelBackground.position.set(-8, -7);
        this._reelBackground.tint = 0x000000;

        this._waysText = new PIXI.Sprite(Mental.assetLoader.getTextures("ways_top")[0]);
        this._waysText.anchor.set(0.5);
        this._waysText.scale.set(1.2);
        this._waysText.position.set((146 + this._waysText.width * 0.5) + 15, this._waysText.height * 0.5);
        this._waysText.tint = 0xaaaaaa;

        // xways Text
        const xwaysText = new PIXI.Sprite(Mental.assetLoader.getTextures("Xways")[0]);
        xwaysText.anchor.set(0.5);
        xwaysText.scale.set(0.65);
        xwaysText.position.set((144 * 3) + xwaysText.width + 5, xwaysText.height * 0.30);

        //xNudge text
        const xNudgeText = new PIXI.Sprite(Mental.assetLoader.getTextures("Xnudge")[0]);
        xNudgeText.anchor.set(0.5);
        xNudgeText.scale.set(0.7);
        xNudgeText.position.set((144 * 3) + xNudgeText.width * 1.3, xNudgeText.height * 0.95);

        //xSplit text
        const xSplitText = new PIXI.Sprite(Mental.assetLoader.getTextures("Xsplit")[0]);
        xSplitText.anchor.set(0.5);
        xSplitText.scale.set(0.7);
        xSplitText.position.set((144 * 3) + xSplitText.width * 0.5, xSplitText.height * 0.95);

        // bottomLocker1
        this._bottomLocker1 = new PIXI.Sprite(Mental.assetLoader.getTextures("bottom_locker")[0]);
        this._bottomLocker1.position.set(144, 494);

        // bottomLocker2
        this._bottomLocker2 = new PIXI.Sprite(Mental.assetLoader.getTextures("bottom_locker")[0]);
        this._bottomLocker2.position.set(432, 494);

        this._reelLayers = new MentalLayerSet("Reels");

        this.createReelsMask();

        this.createReels();

        this._mainView.addChild(this._bottomLocker1,
            this._bottomLocker2, this._reelBackground, this._waysText,
            xSplitText, xNudgeText, xwaysText);
        this._mainView.addChild(this._reelLayers.layers[0]);
        this._mainView.addChild(this._reelLayers.layers[1]);
        this._mainView.addChild(this._reelLayers.layers[2]);
        // this._mainView.addChild(this._reelLayers.layers[3]);
        this.addChild(this._mainView);

        this.onOrientationChanged();
    }

    /**
     * function to create reel area mask
     * individual mask for each reel.
     * @private
     */
    private createReelsMask(): void {
        for (let i: number = 0; i < 5; i++) {
            const mask: PIXI.Graphics = new PIXI.Graphics();
            mask.beginFill(0x000000, 1);
            mask.drawRect(0, 0, this.symbolsSize.x, (i == 3 || i == 1) ? this.symbolsSize.y * 2 : this.symbolsSize.y * 3);
            mask.endFill();
            this._reelMask.push(mask);
        }
    }

    private createReels(): void {
        this._reels = [];
        for (let i: number = 0; i < 5; i++) {
            const reel = new MentalReel(this, i);
            reel.model.init(Mental.model.initData.reels[i]);
            // #271
            reel.model.reelSet = MentalReelSet.getReelSetByName(Mental.model.data.nextMode)[i];
            reel.position.x = this.symbolsSize.x * i;
            // adding mask to reel itself on top most layer.
            reel.layerSet.layers[2].addChild(this._reelMask[i]);
            reel.layerSet.layers[0].mask = this._reelMask[i];
            reel.layerSet.layers[1].mask = this._reelMask[i];

            this._reelLayers.addLayerSet(reel.layerSet);
            this._reels.push(reel);
        }
    }
}